import { useState } from 'react';
import { Ban, Check, Circle, Clock3, RotateCcw, XCircle } from 'lucide-react';
import type { LifecycleTimelineStage } from '@/api/lifecycleTimeline';
import { Card, SectionHeading } from '@/components/ui/Page';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { humanize } from '@/config/status';

const localDate = (value?: string | null) =>
  value ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—';
const displayValue = (value?: string | null) => value?.trim() || '-';

const vendorStageLabels: Record<string, string> = {
  INVITE: 'Invite',
  INVITE_SENT: 'Invite',
  REGISTRATION_IN_PROGRESS: 'Registration in progress',
  REGISTRATION_DRAFT: 'Registration in progress',
  DRAFT: 'Registration in progress',
  REGISTRATION_SUBMITTED: 'Registration submitted',
  SUBMITTED: 'Registration submitted',
  INTERNAL_APPROVAL: 'Internal approval',
  PENDING_APPROVAL: 'Internal approval',
  APPROVAL: 'Internal approval',
  VENDOR_ACTIVE: 'Vendor active',
  ACTIVE: 'Vendor active',
};
const vendorStageLabel = (stage: LifecycleTimelineStage) => {
  const code = stage.code.trim().toUpperCase();
  const label = stage.label.trim().toUpperCase().replaceAll(/[^A-Z0-9]+/g, '_');
  return vendorStageLabels[code] ?? vendorStageLabels[label];
};

const stageIcon = (state: LifecycleTimelineStage['state']) => {
  if (state === 'COMPLETED') return <Check />;
  if (state === 'CURRENT') return <Clock3 />;
  if (state === 'REJECTED') return <XCircle />;
  if (state === 'RETURNED') return <RotateCcw />;
  if (state === 'BLOCKED') return <Ban />;
  return <Circle />;
};

export function LifecycleTimeline({ stages, supplemental, compact = false, horizontal = false }: { stages: LifecycleTimelineStage[]; supplemental?: Record<string, string | null | undefined>; compact?: boolean; horizontal?: boolean }) {
  const ordered = stages.map((stage, index) => ({ stage, index })).sort((a, b) => a.stage.order - b.stage.order || a.index - b.index);
  return <Card className={`lifecycle-timeline-card ${compact ? 'lifecycle-timeline-card--compact' : ''} ${horizontal ? 'lifecycle-timeline-card--horizontal' : ''}`}><SectionHeading title="Lifecycle timeline" description="Completed, current, and upcoming stages" /><div className="lifecycle-timeline">{ordered.map(({ stage, index }) => {
    const role = stage.roleName || stage.roleCode || 'System';
    const complete = stage.state === 'COMPLETED';
    const detail = complete ? stage.actorName ? `${stage.action ? humanize(stage.action) : 'Completed'} by ${stage.actorName}${stage.completedAt ? ` on ${localDate(stage.completedAt)}` : ''}` : `Completed${stage.completedAt ? ` on ${localDate(stage.completedAt)}` : ''}` : stage.state === 'CURRENT' ? `Pending · ${role} owns this step` : stage.state === 'UPCOMING' ? `Upcoming · ${role}` : `${humanize(stage.state)}${stage.actorName ? ` by ${stage.actorName}` : ''}`;
    return <div className={`lifecycle-timeline__stage lifecycle-timeline__stage--${stage.state.toLowerCase()}`} key={`${stage.code}-${index}`}><span className="lifecycle-timeline__icon">{stageIcon(stage.state)}</span><div><div className="lifecycle-timeline__heading"><strong>{stage.label}</strong><StatusBadge status={stage.state} /></div><span>Role: {role}</span><p>{detail}</p>{supplemental?.[stage.code] && <p className="lifecycle-timeline__supplemental">{supplemental[stage.code]}</p>}{stage.comments && <details className="lifecycle-timeline__comment"><summary>View comment</summary><p>{stage.comments}</p></details>}</div></div>;
  })}</div></Card>;
}

export function VendorLifecycleTimeline({ stages }: { stages: LifecycleTimelineStage[] }) {
  const ordered = stages
    .filter((stage) => vendorStageLabel(stage))
    .map((stage) => ({ ...stage, label: vendorStageLabel(stage)! }))
    .sort((a, b) => a.order - b.order);
  const initial = ordered.find((stage) => stage.state === 'CURRENT')
    ?? [...ordered].reverse().find((stage) => stage.state === 'COMPLETED')
    ?? ordered[0];
  const [selectedCode, setSelectedCode] = useState(initial?.code ?? '');
  const selected = ordered.find((stage) => stage.code === selectedCode) ?? initial;

  return <Card className="vendor-lifecycle-timeline-card">
    <SectionHeading title="Lifecycle timeline" description="Click a stage to view its details below." />
    <div className="vendor-lifecycle-timeline" role="list">
      {ordered.map((stage, index) => <button
        type="button"
        role="listitem"
        aria-current={stage.code === selected?.code ? 'step' : undefined}
        className={`vendor-lifecycle-stage vendor-lifecycle-stage--${stage.state.toLowerCase()} ${stage.code === selected?.code ? 'is-selected' : ''}`}
        key={stage.code}
        onClick={() => setSelectedCode(stage.code)}
      >
        <span className="vendor-lifecycle-stage__track"><i>{stageIcon(stage.state)}</i></span>
        <small>{index + 1}</small>
        <strong>{stage.label}</strong>
        <StatusBadge status={stage.state} />
      </button>)}
    </div>
    {selected && <section className="vendor-lifecycle-details">
      <header><div><span>Selected stage details</span><h3>{selected.label}</h3></div><StatusBadge status={selected.state} /></header>
      <div className="vendor-lifecycle-details__grid">
        <div><span>Status</span><strong>{displayValue(selected.state)}</strong></div>
        <div><span>Owner</span><strong>{displayValue(selected.roleName)}</strong></div>
        <div><span>Completed by</span><strong>{displayValue(selected.actorName)}</strong></div>
        <div><span>Completed at</span><strong>{selected.completedAt ? localDate(selected.completedAt) : '-'}</strong></div>
        <div className="vendor-lifecycle-details__wide"><span>Comments</span><strong>{displayValue(selected.comments)}</strong></div>
        <div className="vendor-lifecycle-details__wide"><span>Action</span><strong>{displayValue(selected.action ? humanize(selected.action) : null)}</strong></div>
      </div>
    </section>}
  </Card>;
}
